import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GitMerge, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { resolutionAssistantApi } from '@/services/api/entity-resolution-assistant'
import { parseResolutionSummary, parseResolutionToolSummary } from './entity-resolution'
import { ResolutionReview } from './ResolutionReview'
import { clearFinishedResolutionJob } from './useResolutionAssistant'

const COPY = 'resolutionAssistant'

function useResolutionSummary(result: unknown, artifact: unknown) {
  const { caseId = '' } = useParams<{ caseId: string }>()
  const client = useQueryClient()
  const [freshReview, setFreshReview] = useState<{
    summary: ReturnType<typeof parseResolutionSummary>
    revision: number
  } | null>(null)
  const refresh = useMutation({
    mutationFn: () => resolutionAssistantApi.summary(caseId),
    onSuccess: data => {
      const summary = parseResolutionSummary(data, caseId)
      if (!summary) return
      clearFinishedResolutionJob(client, caseId, data.snapshot_id)
      setFreshReview(previous => ({ summary, revision: (previous?.revision ?? 0) + 1 }))
    },
  })
  return {
    summary:
      freshReview?.summary?.case_id === caseId
        ? freshReview.summary
        : parseResolutionToolSummary(result, artifact, caseId),
    revision: freshReview?.revision ?? 0,
    refresh,
    caseId,
  }
}

export function ResolutionToolCard({
  result,
  artifact,
  status,
}: {
  result?: unknown
  artifact?: unknown
  status: { type: string }
}) {
  const { t } = useTranslation()
  const { summary, revision, refresh, caseId } = useResolutionSummary(result, artifact)
  const running = status.type === 'running'
  return (
    <section
      aria-label={t(`${COPY}.title`)}
      className="my-2 space-y-3 rounded-lg border border-border bg-card p-3 text-sm"
    >
      <h3 className="flex items-center gap-2 font-medium">
        <GitMerge aria-hidden className="size-4" />
        {t(`${COPY}.title`)}
      </h3>
      {summary && !running ? (
        <ResolutionReview
          key={`${summary.snapshot_id}:${revision}`}
          summary={summary}
          refresh={{ pending: refresh.isPending, run: () => refresh.mutate() }}
        />
      ) : (
        <ResolutionToolPlaceholder running={running} />
      )}
      <ResolutionRefreshState refresh={refresh} available={!running && !summary && !!caseId} />
    </section>
  )
}

function ResolutionRefreshState({
  refresh,
  available,
}: {
  refresh: ReturnType<typeof useResolutionSummary>['refresh']
  available: boolean
}) {
  const { t } = useTranslation()
  return (
    <>
      {refresh.isError && (
        <p role="alert" className="text-destructive">
          {t(`${COPY}.refreshFailed`)}
        </p>
      )}
      {available && (
        <Button
          variant="outline"
          size="sm"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          {t(`${COPY}.refresh`)}
        </Button>
      )}
    </>
  )
}

function ResolutionToolPlaceholder({ running }: { running: boolean }) {
  const { t } = useTranslation()
  return running ? (
    <p role="status" className="flex items-center gap-2">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {t(`${COPY}.loading`)}
    </p>
  ) : (
    <p role="alert" className="text-destructive">
      {t(`${COPY}.unavailable`)}
    </p>
  )
}

export const EntityResolutionToolUI = makeAssistantToolUI<Record<string, never>, unknown>({
  toolName: 'summarize_entity_resolution',
  render: ResolutionToolCard,
})
